import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import bs58 from "bs58";
import { X } from "lucide-react";
import { slurpApi } from "../../lib/slurpApi";

const MAX_FILE_SIZE = 1024 * 1024;            // 1 MB cap (matches launch flow)
const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/;

/**
 * Shared profile editor — used in two contexts:
 *   mode="welcome"  → first-time wallet connect, has a "Skip for now" CTA
 *   mode="edit"     → returning user editing from their profile page
 *
 * On submit:
 *   1. Validate fields client-side
 *   2. Wallet signs `slurp-profile:{ts}` via adapter.signMessage
 *   3. POST to /api/profiles/:wallet with X-Profile-* headers
 *   4. onSaved(profile) called; modal closes
 *
 * Skip on welcome flips a localStorage flag so the modal doesn't show
 * again for that wallet on this device.
 */
export default function ProfileFormModal({
  mode = "welcome",
  initial = null,         // existing profile or null for first-time
  onClose,
  onSaved,
}) {
  const wallet = useWallet();
  const walletStr = wallet.publicKey?.toBase58() ?? "";

  const [username, setUsername] = useState(initial?.username ?? "");
  const [bio, setBio] = useState(initial?.bio ?? "");
  const [imageUrl, setImageUrl] = useState(initial?.image_url ?? "");
  const [twitter, setTwitter] = useState(initial?.twitter ?? "");
  const [website, setWebsite] = useState(initial?.website ?? "");
  const [telegram, setTelegram] = useState(initial?.telegram ?? "");
  const [github, setGithub] = useState(initial?.github ?? "");

  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [usernameStatus, setUsernameStatus] = useState({ state: "idle", msg: "" });
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [xBusy, setXBusy] = useState(false);
  const fileRef = useRef(null);
  const checkSeq = useRef(0);

  // Verify-with-X — sign a short challenge then redirect to /api/auth/x/start,
  // which kicks the user into X's OAuth flow. They land back on
  // /profile/<wallet>?x_verified=1 when done.
  const onVerifyX = async () => {
    if (!wallet.signMessage || !walletStr) return;
    setXBusy(true);
    try {
      const ts = Date.now();
      const message = new TextEncoder().encode(`slurp-verify-x:${ts}`);
      const sigBytes = await wallet.signMessage(message);
      const sig = bs58.encode(sigBytes);
      window.location.assign(slurpApi.xVerifyStartUrl(walletStr, ts, sig));
    } catch (err) {
      setXBusy(false);
      setSaveError(err?.message || "Couldn't start verification");
    }
  };

  // Clear the verified flag. User can then verify again with a different
  // X account, or just leave their handle unverified.
  const onUnlinkX = async () => {
    if (!wallet.signMessage || !walletStr) return;
    if (!window.confirm("Unlink your verified X account? You can re-verify after.")) return;
    setXBusy(true);
    try {
      const ts = Date.now();
      const message = new TextEncoder().encode(`slurp-verify-x-unlink:${ts}`);
      const sigBytes = await wallet.signMessage(message);
      const sig = bs58.encode(sigBytes);
      await slurpApi.xVerifyUnlink(walletStr, {
        "X-Profile-Ts": String(ts),
        "X-Profile-Sig": sig,
      });
      // Refresh the page so the form picks up the cleared state without a
      // full router re-mount dance.
      window.location.reload();
    } catch (err) {
      setSaveError(err?.message || "Couldn't unlink");
    } finally {
      setXBusy(false);
    }
  };

  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Username availability — debounce live as user types.
  useEffect(() => {
    if (!username) { setUsernameStatus({ state: "idle", msg: "" }); return; }
    if (!USERNAME_RE.test(username)) {
      setUsernameStatus({ state: "bad", msg: "3–20 chars, letters / numbers / underscore" });
      return;
    }
    if (initial?.username && initial.username.toLowerCase() === username.toLowerCase()) {
      setUsernameStatus({ state: "ok", msg: "Your current username" });
      return;
    }
    setUsernameStatus({ state: "checking", msg: "Checking…" });
    const seq = ++checkSeq.current;
    const t = setTimeout(async () => {
      try {
        const r = await slurpApi.checkProfileUsername(username, walletStr);
        if (seq !== checkSeq.current) return;
        if (r.available) {
          setUsernameStatus({ state: "ok", msg: "Available" });
        } else {
          setUsernameStatus({ state: "bad", msg: r.message || "Not available" });
        }
      } catch (err) {
        if (seq !== checkSeq.current) return;
        setUsernameStatus({ state: "bad", msg: "Couldn't check, try again" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username, walletStr, initial?.username]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);
    if (file.size > MAX_FILE_SIZE) {
      setImageError("Max 1 MB. Crop or compress and try again.");
      return;
    }
    if (!/^image\/(png|jpeg|jpg|webp|gif)$/.test(file.type)) {
      setImageError("PNG, JPG, WebP, or GIF only.");
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, publicUrl } = await slurpApi.signUpload({
        contentType: file.type,
        size: file.size,
        prefix: "profiles",
      });
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setImageUrl(publicUrl);
    } catch (err) {
      setImageError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const usernameOk = useMemo(() => {
    if (!username) return true;       // username is optional
    return usernameStatus.state === "ok";
  }, [username, usernameStatus.state]);

  const submit = async () => {
    if (!wallet?.publicKey || !wallet?.signMessage) {
      setSaveError("Wallet doesn't support signing");
      return;
    }
    if (!usernameOk) {
      setSaveError("Pick a different username");
      return;
    }
    setSubmitting(true);
    setSaveError(null);
    try {
      const ts = Date.now();
      const message = new TextEncoder().encode(`slurp-profile:${ts}`);
      const sigBytes = await wallet.signMessage(message);
      const sig = bs58.encode(sigBytes);
      const headers = {
        "X-Profile-Address": walletStr,
        "X-Profile-Ts": String(ts),
        "X-Profile-Sig": sig,
      };
      const r = await slurpApi.saveProfile(
        walletStr,
        {
          username: username || null,
          bio: bio || null,
          image_url: imageUrl || null,
          twitter: twitter || null,
          website: website || null,
          telegram: telegram || null,
          github: github || null,
        },
        headers
      );
      onSaved?.(r.profile);
      onClose?.();
    } catch (err) {
      setSaveError(err?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => {
    try {
      localStorage.setItem(`slurp:profile_skipped:${walletStr}`, "1");
    } catch { /* private mode */ }
    onClose?.();
  };

  const heading = mode === "welcome" ? "Welcome to Slurp" : "Edit profile";
  const sub = mode === "welcome"
    ? "Give the network a face. Optional, you can skip and set this up later."
    : "Update your profile. All fields optional.";

  return (
    <div className="pm-modal__bg" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pm-modal pm-profile-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pm-modal__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h3 className="pm-modal__title">{heading}</h3>
        <p className="pm-profile-modal__sub">{sub}</p>

        {/* Avatar — click the image itself to upload (no separate button) */}
        <div className="pm-profile-form__avatar-row">
          <button
            type="button"
            className="pm-profile-form__avatar pm-profile-form__avatar--button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label={imageUrl ? "Change profile picture" : "Upload profile picture"}
          >
            {imageUrl ? (
              <img src={imageUrl} alt="Profile" />
            ) : (
              <div
                className="pm-profile-form__avatar-ph"
                style={{ background: gradientFor(walletStr) }}
              >
                {walletStr.slice(0, 2).toUpperCase()}
              </div>
            )}
            <span className="pm-profile-form__avatar-overlay">
              {uploading ? "Uploading…" : imageUrl ? "Change" : "Upload"}
            </span>
          </button>
          <div className="pm-profile-form__avatar-actions">
            <span className="pm-profile-form__avatar-hint">
              {imageUrl
                ? "Click the photo to swap it."
                : "Click the photo to upload one. PNG, JPG, WebP — 1 MB max."}
            </span>
            {imageUrl && (
              <button
                type="button"
                className="pm-profile-form__btn-link"
                onClick={() => setImageUrl("")}
                disabled={uploading}
              >
                Remove photo
              </button>
            )}
            {imageError && (
              <div className="pm-profile-form__field-err">{imageError}</div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={onPickFile}
            />
          </div>
        </div>

        <Field
          label="Username"
          hint="Public handle. Optional. 3–20 chars, letters / numbers / underscore."
        >
          <input
            type="text"
            className="pm-profile-form__input"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
            placeholder="e.g. slurpy"
            maxLength={20}
          />
          {username && (
            <div className={`pm-profile-form__status pm-profile-form__status--${usernameStatus.state}`}>
              {usernameStatus.msg}
            </div>
          )}
        </Field>

        <Field label="Description" hint="Tell people who you are. Max 200 chars.">
          <textarea
            className="pm-profile-form__input pm-profile-form__textarea"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Diamond hand from day one"
            maxLength={200}
            rows={3}
          />
          <div className="pm-profile-form__counter">{bio.length}/200</div>
        </Field>

        <Field label="X (Twitter)">
          {initial?.x_verified_username ? (
            // Verified state — handle is locked to whatever X returned.
            // Re-verify or unlink to change it.
            <div className="pm-profile-form__x-verified">
              <span className="pm-profile-form__x-verified-pill" title="X account ownership verified">
                <span aria-hidden="true">✓</span>
                @{initial.x_verified_username}
                <span className="pm-profile-form__x-verified-sub">verified</span>
              </span>
              <button
                type="button"
                className="pm-profile-form__x-action pm-profile-form__x-action--ghost"
                onClick={onUnlinkX}
                disabled={xBusy}
              >
                {xBusy ? "…" : "Unlink"}
              </button>
            </div>
          ) : (
            <>
              <div className="pm-profile-form__prefix">
                <span className="pm-profile-form__prefix-label">x.com/</span>
                <input
                  className="pm-profile-form__prefix-input"
                  type="text"
                  value={twitter.replace(/^https?:\/\/(x|twitter)\.com\//i, "")}
                  onChange={(e) => {
                    const handle = e.target.value
                      .replace(/^@/, "")
                      .replace(/^https?:\/\/(x|twitter)\.com\//i, "")
                      .trim();
                    setTwitter(handle ? `https://x.com/${handle}` : "");
                  }}
                  placeholder="handle"
                />
              </div>
              <div className="pm-profile-form__x-row">
                <button
                  type="button"
                  className="pm-profile-form__x-action"
                  onClick={onVerifyX}
                  disabled={xBusy || !wallet.signMessage}
                >
                  {xBusy ? "Opening X…" : "Verify with X"}
                </button>
                <span className="pm-profile-form__x-hint">
                  Proves you own this handle
                </span>
              </div>
            </>
          )}
        </Field>

        <Field label="Website">
          <div className="pm-profile-form__prefix">
            <span className="pm-profile-form__prefix-label">https://</span>
            <input
              className="pm-profile-form__prefix-input"
              type="text"
              value={website.replace(/^https?:\/\//i, "")}
              onChange={(e) => {
                const stripped = e.target.value.replace(/^https?:\/\//i, "").trim();
                setWebsite(stripped ? `https://${stripped}` : "");
              }}
              placeholder="your.site"
            />
          </div>
        </Field>

        <Field label="Telegram">
          <div className="pm-profile-form__prefix">
            <span className="pm-profile-form__prefix-label">t.me/</span>
            <input
              className="pm-profile-form__prefix-input"
              type="text"
              value={telegram.replace(/^https?:\/\/t\.me\//i, "")}
              onChange={(e) => {
                const handle = e.target.value
                  .replace(/^@/, "")
                  .replace(/^https?:\/\/t\.me\//i, "")
                  .trim();
                setTelegram(handle ? `https://t.me/${handle}` : "");
              }}
              placeholder="group_or_handle"
            />
          </div>
        </Field>

        <Field label="GitHub">
          <div className="pm-profile-form__prefix">
            <span className="pm-profile-form__prefix-label">github.com/</span>
            <input
              className="pm-profile-form__prefix-input"
              type="text"
              value={github.replace(/^https?:\/\/github\.com\//i, "")}
              onChange={(e) => {
                const handle = e.target.value
                  .replace(/^@/, "")
                  .replace(/^https?:\/\/github\.com\//i, "")
                  .trim();
                setGithub(handle ? `https://github.com/${handle}` : "");
              }}
              placeholder="username"
            />
          </div>
        </Field>

        {saveError && (
          <div className="pm-profile-form__error">{saveError}</div>
        )}

        <div className="pm-profile-form__actions">
          {mode === "welcome" && (
            <button
              type="button"
              className="pm-profile-form__btn-ghost"
              onClick={skip}
              disabled={submitting}
            >
              Skip for now
            </button>
          )}
          <button
            type="button"
            className="pm-profile-form__btn-primary"
            onClick={submit}
            disabled={submitting || uploading || !usernameOk}
          >
            {submitting ? "Saving…" : mode === "welcome" ? "Save profile" : "Save changes"}
          </button>
        </div>

        <p className="pm-profile-form__note">
          You'll be asked to sign a message with your wallet. No SOL or fees.
        </p>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="pm-profile-form__field">
      <span className="pm-profile-form__label">{label}</span>
      {hint && <span className="pm-profile-form__hint">{hint}</span>}
      {children}
    </label>
  );
}

/**
 * Deterministic gradient per wallet so wallets without an avatar still
 * get a unique-ish coloured tile rather than a blank grey square.
 */
export function gradientFor(wallet) {
  if (!wallet) return "linear-gradient(135deg, #2c2620, #1a1714)";
  let hash = 0;
  for (let i = 0; i < wallet.length; i++) {
    hash = ((hash << 5) - hash) + wallet.charCodeAt(i);
    hash |= 0;
  }
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 50) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 65%, 55%), hsl(${h2}, 70%, 45%))`;
}
